// Isolated clock component to prevent unnecessary re-renders
import { useCallback, useEffect, useState } from 'react';
import { startOfDay } from 'date-fns';
import { translateToTz } from '@/util/dateTime';

/**
 * Returns start-of-day for the hospital's timezone (falls back to device
 * timezone when the timezone id is not yet available from the scope query).
 */
const getToday = (timezone?: string): Date => {
  const now = new Date();
  const adjusted = timezone ? translateToTz(timezone)(now) : now;
  return startOfDay(adjusted);
};

export const useClock = (timezone?: string) => {
  const [currentDate, setCurrentDate] = useState(() => getToday(timezone));

  // Render-time staleness check: if this component re-renders for ANY reason
  // (subscription data, polling, parent re-render) and the date is stale,
  // correct it immediately. React handles setState-during-render by restarting
  // the render with the new state, same pattern as the dayIsOver staleness guard.
  // This is the last line of defense for kiosk screens where setInterval may
  // have silently stopped firing.
  const today = getToday(timezone);
  if (currentDate.getTime() !== today.getTime()) {
    setCurrentDate(today);
  }

  const checkDateChange = useCallback(() => {
    const today = getToday(timezone);
    setCurrentDate(prev => {
      if (prev.getTime() !== today.getTime()) {
        return today;
      }
      return prev;
    });
  }, [timezone]);

  useEffect(() => {
    // Check every minute if the day has changed
    const interval = setInterval(checkDateChange, 1000 * 60);

    // Also check when the tab regains visibility or focus.
    // Kiosk screens can miss setInterval ticks when the browser freezes
    // the tab (device sleep, power management, memory pressure).
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkDateChange();
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('focus', checkDateChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('focus', checkDateChange);
    };
  }, [checkDateChange]);

  return currentDate;
};
